/**
 * Viewer model — the "juice" meter. Audience starts at the competition's base
 * and drifts with the rolling Director Rating: slow growth on quality and big
 * moments covered, sharp drops on MISSED IT, a little random noise on top.
 */
import type { Rng } from '../core/contracts';

/** Base audiences per career tier (mirrors meta/career.ts). */
export const TIER_AUDIENCE = {
  sunday: 4_000,
  regional: 120_000,
  national: 2_100_000,
  continental: 18_000_000,
  cup_final: 300_000_000,
} as const;

/** Guess a base audience from a competition name ("Regional League — Matchday 4"). */
export function baseAudienceFor(competition: string): number {
  const c = competition.toLowerCase();
  if (c.includes('sunday')) return TIER_AUDIENCE.sunday;
  if (c.includes('regional')) return TIER_AUDIENCE.regional;
  if (c.includes('continental') || c.includes('champions')) return TIER_AUDIENCE.continental;
  if (c.includes('final')) return TIER_AUDIENCE.cup_final;
  if (c.includes('national') || c.includes('premier')) return TIER_AUDIENCE.national;
  return 250_000;
}

export interface ViewerInput {
  /** Composite Director Rating 0..100. */
  rating: number;
  /** Key moments covered this update (each gives a small bump). */
  bigCovered?: number;
  /** MISSED IT count this update (each is a sharp drop). */
  missed?: number;
  /** Current streak — long streaks add a growth trickle. */
  streak?: number;
  /** Live intensity 0..1 — exciting play pulls channel-hoppers in. */
  intensity?: number;
}

export interface ViewerModel {
  update(dt: number, input: ViewerInput): number;
  viewers(): number;
  peak(): number;
  base(): number;
}

export function createViewerModel(baseAudience: number, rng: Rng): ViewerModel {
  const base = Math.max(1, baseAudience);
  let v = base;
  let peak = base;
  let noise = 0; // slow-moving random walk, fraction of audience

  /** Where the audience settles for a given rating: 50 → 0.8x, 70 → 1.0x, 95 → 1.3x. */
  const targetFor = (rating: number, streak: number, intensity: number) => {
    const r = Math.max(0, Math.min(100, rating));
    const mult = r >= 70 ? 1 + (r - 70) * 0.012 : 1 - (70 - r) * 0.01;
    const streakBoost = 1 + Math.min(streak, 20) * 0.003;
    const excitement = 1 + intensity * 0.03;
    return base * Math.max(0.25, mult) * streakBoost * excitement;
  };

  return {
    update(dt, input) {
      if (dt <= 0) return v;
      const target = targetFor(input.rating, input.streak ?? 0, input.intensity ?? 0);
      // Growth is slow (audiences take time to find you); decline is quicker.
      const tau = target > v ? 45 : 25;
      v += (target - v) * (1 - Math.exp(-dt / tau));
      for (let i = 0; i < (input.bigCovered ?? 0); i++) v *= 1.012;
      for (let i = 0; i < (input.missed ?? 0); i++) v *= 0.93;
      noise = noise * Math.exp(-dt / 8) + rng.gaussian(0, 0.0012) * Math.sqrt(dt * 30);
      const shown = Math.max(1, v * (1 + noise));
      peak = Math.max(peak, shown);
      return Math.round(shown);
    },
    viewers: () => Math.round(Math.max(1, v * (1 + noise))),
    peak: () => Math.round(peak),
    base: () => base,
  };
}
